/**
 * A small Server-Sent Events parser for a POST response read through fetch + ReadableStream (EventSource can only
 * GET). Follows the WHATWG event-stream rules that matter here:
 *
 * - lines end in LF, CRLF or CR, and a chunk may end anywhere (mid-line, mid-CRLF, mid-event);
 * - `event:` names the event (default "message"), each `data:` line adds a line to the data (joined with LF);
 * - one leading space after the colon is dropped; lines starting with ":" are comments (keep-alives);
 * - a blank line dispatches the event; an event with no data line is not dispatched;
 * - `id:` and `retry:` are accepted and ignored.
 */
export interface SseEvent {
  event: string;
  data: string;
}

export interface SseParser {
  /** Feed the next decoded chunk of the stream. */
  feed(chunk: string): void;
  /** The stream ended: dispatch a final event that had no trailing blank line. */
  end(): void;
}

export function createSseParser(onEvent: (e: SseEvent) => void): SseParser {
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];
  // A chunk that ended in CR may be the first half of a CRLF: the LF that follows is not a second line end.
  let pendingCr = false;

  const dispatch = () => {
    if (dataLines.length > 0) {
      onEvent({ event: eventName || 'message', data: dataLines.join('\n') });
    }
    eventName = '';
    dataLines = [];
  };

  const processLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
    // id / retry / unknown fields: ignored
  };

  return {
    feed(chunk: string) {
      if (!chunk) return;
      let text = chunk;
      if (pendingCr && text.startsWith('\n')) text = text.slice(1);
      pendingCr = false;
      buffer += text;
      let start = 0;
      for (let i = 0; i < buffer.length; i++) {
        const ch = buffer[i];
        if (ch === '\n' || ch === '\r') {
          processLine(buffer.slice(start, i));
          if (ch === '\r') {
            if (i + 1 < buffer.length) {
              if (buffer[i + 1] === '\n') i++;
            } else {
              pendingCr = true;
            }
          }
          start = i + 1;
        }
      }
      buffer = buffer.slice(start);
    },
    end() {
      if (buffer !== '') {
        processLine(buffer);
        buffer = '';
      }
      dispatch();
    },
  };
}
